import type { TransactionRequest, TransactionResponse } from '@ethersproject/abstract-provider'
import { BigNumber, Contract, errors, Signer } from 'ethers'
import { useCallback, useState } from 'react'
import { ContractFunctionNames, TransactionState, TransactionStatus, TypedContract } from './types'

/**
 * @internal
 */
export async function estimateTransactionGasLimit(
    transactionRequest: TransactionRequest | undefined,
    signer: Signer | undefined,
    gasLimitBufferPercentage: number
) {
    if (!signer || !transactionRequest) {
        return undefined
    }
    try {
        const estimatedGas = transactionRequest.gasLimit
            ? BigNumber.from(transactionRequest.gasLimit)
            : await signer.estimateGas(transactionRequest)
        return estimatedGas?.mul(gasLimitBufferPercentage + 100).div(100)
    } catch (err: any) {
        console.error(err)
        return undefined
    }
}

/**
 * @internal
 */
export async function estimateContractFunctionGasLimit(
    contractWithSigner: Contract,
    functionName: string,
    args: any[],
    gasLimitBufferPercentage: number
): Promise<BigNumber | undefined> {
    try {
        const estimatedGas = await contractWithSigner.estimateGas[functionName](...args)
        const gasLimit = estimatedGas?.mul(gasLimitBufferPercentage + 100).div(100)
        return gasLimit
    } catch (err: any) {
        console.error(err)
        return undefined
    }
}

const isDroppedAndReplaced = (e: any) =>
    e?.code === errors.TRANSACTION_REPLACED &&
    e?.replacement &&
    (e?.reason === 'repriced' || e?.cancelled === false)

export function usePromiseTransaction<T extends TypedContract, FN extends ContractFunctionNames<T>>(
    chainId: number | undefined,
    contractFunction: FN
) {
    const [state, setState] = useState<TransactionStatus>({
        status: 'None',
        transactionName: contractFunction,
    })

    const resetState = useCallback(() => {
        setState(({ transactionName }) => ({ status: 'None', transactionName }))
    }, [setState])

    const promiseTransaction = useCallback(
        async (transactionPromise: Promise<TransactionResponse>) => {
            const handleNonContractWallet = async (
                transactionPromise: Promise<TransactionResponse>
            ) => {
                if (!chainId) return

                const transaction = await transactionPromise

                setState((prevState) => ({ ...prevState, transaction, status: 'Mining' }))

                const receipt = await transaction.wait()

                setState({
                    receipt,
                    transaction,
                    status: 'Success',
                    chainId,
                    transactionName: contractFunction,
                })
                return { transaction, receipt }
            }

            if (!chainId) return
            let transaction: TransactionResponse | undefined = undefined
            try {
                setState({
                    status: 'PendingSignature',
                    chainId,
                    transactionName: contractFunction,
                })

                const result = await handleNonContractWallet(transactionPromise)

                transaction = result?.transaction
                return result?.receipt
            } catch (e: any) {
                const parsedErrorCode = parseInt(
                    e.error?.data?.code ?? e.error?.code ?? e.data?.code ?? e.code
                )
                const errorCode = isNaN(parsedErrorCode) ? undefined : parsedErrorCode
                const errorHash = e?.error?.data?.originalError?.data ?? e?.error?.data
                const errorMessage =
                    e.error?.data?.message ??
                    e.error?.message ??
                    e.reason ??
                    e.data?.message ??
                    e.message
                if (transaction) {
                    const droppedAndReplaced = isDroppedAndReplaced(e)

                    if (droppedAndReplaced) {
                        const status: TransactionState = e.receipt.status === 0 ? 'Fail' : 'Success'

                        setState((prevState) => ({
                            ...prevState,
                            status,
                            transaction: e.replacement,
                            originalTransaction: transaction,
                            receipt: e.receipt,
                            transactionName: e.replacement?.transactionName,
                            errorMessage,
                            errorCode,
                            errorHash,
                            chainId,
                        }))
                    } else {
                        setState({
                            status: 'Fail',
                            transaction,
                            receipt: e.receipt,
                            errorMessage,
                            errorCode,
                            errorHash,
                            chainId,
                            transactionName: contractFunction,
                        })
                    }
                } else {
                    setState({
                        status: 'Exception',
                        errorMessage,
                        errorCode,
                        errorHash,
                        chainId,
                        transactionName: contractFunction,
                    })
                }
                return undefined
            }
        },
        [chainId, contractFunction]
    )

    return { promiseTransaction, state, resetState }
}
